import { Fragment, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import TableSortLabel from '@mui/material/TableSortLabel';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import { toast } from 'react-toastify';
import { Calendar, CheckCircle2, Search, RefreshCcw, FileText, ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';
import api from '../services/api';
import { useJob } from '../context/JobContext';

const StyledCard = styled(Card)(() => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden'
}));

const formatDate = (value) => {
  if (!value) return '-';
  const raw = value instanceof Date ? value.toISOString().slice(0, 10) : String(value);
  const normalized = raw.slice(0, 10);
  const parts = normalized.split('-');
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return raw;
  return parsed.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatCurrency = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(n);
};

const getTodayIsoDate = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const HeaderHelp = ({ label, help }) => {
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, whiteSpace: 'nowrap' }}>
      <span>{label}</span>
      {help ? (
        <Tooltip title={help} arrow>
          <IconButton
            size="small"
            onClick={(e) => e.stopPropagation()}
            sx={{ p: 0.25, borderRadius: '10px', color: 'text.secondary' }}
          >
            <HelpCircle size={14} />
          </IconButton>
        </Tooltip>
      ) : null}
    </Box>
  );
};

export default function PraticheCarichiContabile() {
  const { addJob } = useJob();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [referenceDate, setReferenceDate] = useState(getTodayIsoDate());
  const [totalContratti, setTotalContratti] = useState(0);
  const [totalClienti, setTotalClienti] = useState(0);
  const [reportLoading, setReportLoading] = useState(false);
  const [orderBy, setOrderBy] = useState('contratti_totale');
  const [order, setOrder] = useState('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [introOpen, setIntroOpen] = useState(true);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [expandedContabileId, setExpandedContabileId] = useState(null);
  const [contractsByContabileId, setContractsByContabileId] = useState({});
  const [contractsLoadingByContabileId, setContractsLoadingByContabileId] = useState({});
  const [rowPdfLoadingByContabileId, setRowPdfLoadingByContabileId] = useState({});

  const handleStartCarichiContabileReportJob = async (detailMode) => {
    try {
      setReportLoading(true);
      const payload = {
        filters: {
          reference_date: referenceDate || '',
          contabile_search: search || '',
          detail_mode: detailMode || 'summary'
        }
      };
      const res = await api.post('/reports/pratiche-carichi-contabile', payload);
      if (res.data && res.data.jobId) {
        addJob(res.data.jobId, 'pratiche_carichi_contabile_report', {
          meta: {
            referenceDate,
            contabileSearch: search || ''
          }
        });
      }
    } catch (error) {
      console.error('Error starting carichi contabile report job:', error);
      toast.error("Errore durante l'avvio del report PDF");
    } finally {
      setReportLoading(false);
    }
  };

  const fetchData = async ({ dateOverride, contabileSearchOverride } = {}) => {
    try {
      setLoading(true);
      const effectiveDate = dateOverride || referenceDate || getTodayIsoDate();
      const params = { reference_date: effectiveDate };
      const contabileSearch = String(contabileSearchOverride ?? search ?? '').trim();
      if (contabileSearch) {
        params.contabile_search = contabileSearch;
      }
      const res = await api.get('/negotiation/monitoring/workloads/contabili', { params });
      const data = res.data || {};
      setRows(Array.isArray(data.items) ? data.items : []);
      setTotalContratti(Number(data.totalContratti || 0));
      setTotalClienti(Number(data.totalClienti || 0));
      setReferenceDate(data.referenceDate || effectiveDate);
    } catch (error) {
      console.error('Error fetching carichi per contabile:', error);
      toast.error('Errore nel caricamento dei carichi per contabile');
    } finally {
      setLoading(false);
    }
  };

  const fetchContractsByContabile = async (contabileId) => {
    const safeId = Number(contabileId) || 0;
    if (!safeId) return;
    try {
      setContractsLoadingByContabileId((prev) => ({ ...prev, [safeId]: true }));
      const res = await api.get(`/negotiation/monitoring/workloads/contabili/${safeId}/contratti`, {
        params: { reference_date: referenceDate || '' }
      });
      const data = res.data || {};
      setContractsByContabileId((prev) => ({
        ...prev,
        [safeId]: Array.isArray(data.items) ? data.items : []
      }));
    } catch (error) {
      console.error('Error fetching contratti contabile detail:', error);
      toast.error('Errore nel caricamento del dettaglio contratti');
      setContractsByContabileId((prev) => ({ ...prev, [safeId]: [] }));
    } finally {
      setContractsLoadingByContabileId((prev) => ({ ...prev, [safeId]: false }));
    }
  };

  const handleToggleContracts = async (row) => {
    const contabileId = Number(row?.contabile_id) || 0;
    if (!contabileId) return;
    const nextExpanded = expandedContabileId === contabileId ? null : contabileId;
    setExpandedContabileId(nextExpanded);
    if (nextExpanded && !Array.isArray(contractsByContabileId[contabileId])) {
      await fetchContractsByContabile(contabileId);
    }
  };

  const handleStartContabileDetailPdfJob = async (row) => {
    const contabileId = Number(row?.contabile_id) || 0;
    if (!contabileId) return;
    if (rowPdfLoadingByContabileId[contabileId]) return;
    setRowPdfLoadingByContabileId((prev) => ({ ...prev, [contabileId]: true }));
    try {
      const res = await api.post('/reports/pratiche-carichi-contabile-detail', {
        filters: {
          reference_date: referenceDate,
          contabile_id: contabileId,
          contabile_name: row?.contabile_name || '',
          contabile_email: row?.contabile_email || ''
        }
      });
      const jobId = res?.data?.jobId;
      if (jobId) {
        addJob(jobId, 'pratiche_carichi_contabile_detail_report', {
          meta: {
            reference_date: referenceDate,
            contabile_id: contabileId
          }
        });
        toast.success('Elaborazione PDF avviata. Lo trovi nella campanella.');
      } else {
        toast.error('Impossibile avviare il PDF per il contabile selezionato.');
      }
    } catch (e) {
      toast.error('Errore durante avvio PDF.');
    } finally {
      setRowPdfLoadingByContabileId((prev) => ({ ...prev, [contabileId]: false }));
    }
  };

  useEffect(() => {
    fetchData({ dateOverride: referenceDate });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handle = setTimeout(() => {
      fetchData({
        dateOverride: referenceDate,
        contabileSearchOverride: search
      });
    }, 350);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const sortedRows = useMemo(() => {
    const arr = [...(rows || [])];
    arr.sort((a, b) => {
      const valueA = a[orderBy];
      const valueB = b[orderBy];
      if (valueA == null) return 1;
      if (valueB == null) return -1;
      if (
        orderBy === 'contratti_totale' ||
        orderBy === 'contratti_con_recesso' ||
        orderBy === 'contratti_chiusi' ||
        orderBy === 'contratti_multi_legali' ||
        orderBy === 'contratti_percentuale' ||
        orderBy === 'clienti_totale' ||
        orderBy === 'clienti_con_recesso' ||
        orderBy === 'clienti_gestiti' ||
        orderBy === 'clienti_percentuale'
      ) {
        return order === 'asc' ? Number(valueA) - Number(valueB) : Number(valueB) - Number(valueA);
      }
      const strA = String(valueA).toLowerCase();
      const strB = String(valueB).toLowerCase();
      if (strA < strB) return order === 'asc' ? -1 : 1;
      if (strA > strB) return order === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [rows, orderBy, order]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const paginatedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedRows.slice(start, start + rowsPerPage);
  }, [sortedRows, page, rowsPerPage]);

  return (
    <Box sx={{ p: 3 }}>
      <Dialog open={introOpen} onClose={() => setIntroOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Carichi per Contabile</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Calendar size={18} color="#2563eb" />
            <Typography>
              Questa pagina mostra la distribuzione dei contratti per referente contabile fino alla data selezionata.
            </Typography>
          </Box>
          <Box sx={{ display: 'grid', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircle2 size={16} color="#16a34a" />
              <Typography variant="body2" color="text.secondary">
                Sono inclusi i contratti (dossier) con pratiche assegnate fino alla data selezionata (indipendentemente dallo stato).
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircle2 size={16} color="#2563eb" />
              <Typography variant="body2" color="text.secondary">
                “Contratti con recesso” conta i dossier con status = failed e motivazione_fallimento in (lasciare, recesso_nei_tempi, perseguire).
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircle2 size={16} color="#f59e0b" />
              <Typography variant="body2" color="text.secondary">
                “Multi-legali” conta i dossier che hanno 2+ legali distinti sulle negotiation (soft_delete=0).
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircle2 size={16} color="#2563eb" />
              <Typography variant="body2" color="text.secondary">
                “Contratti chiusi” conta i dossier in cui tutte le pratiche (negoziazioni) del dossier sono status = closed.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircle2 size={16} color="#0f172a" />
              <Typography variant="body2" color="text.secondary">
                Il bottone “Report PDF” genera il report e lo rende disponibile nella campanella.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setIntroOpen(false)}>
            Ho capito
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={reportDialogOpen} onClose={() => setReportDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Report PDF</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1.2 }}>
            Vuoi generare il report in versione sintetica o con dettaglio analitico (contratti per contabile)?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Il report verrà preparato in background e sarà scaricabile dalla campanella.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setReportDialogOpen(false)} disabled={reportLoading}>
            Annulla
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              setReportDialogOpen(false);
              handleStartCarichiContabileReportJob('summary');
            }}
            disabled={reportLoading}
          >
            Senza dettaglio
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setReportDialogOpen(false);
              handleStartCarichiContabileReportJob('analytic');
            }}
            disabled={reportLoading}
          >
            Con dettaglio
          </Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              backgroundColor: '#e0e7ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Calendar size={18} color="#4f46e5" />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 900, lineHeight: 1.1 }}>
              Carichi per Contabile
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Distribuzione contratti per referente contabile (data: {formatDate(referenceDate)})
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<FileText size={16} />}
            disabled={reportLoading}
            onClick={() => setReportDialogOpen(true)}
          >
            Report PDF
          </Button>
          <Button
            variant="contained"
            startIcon={<RefreshCcw size={16} />}
            onClick={() =>
              fetchData({
                dateOverride: referenceDate,
                contabileSearchOverride: search
              })
            }
            disabled={loading}
          >
            Aggiorna
          </Button>
        </Box>
      </Box>

      <StyledCard>
        <Box sx={{ p: 2.2, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'nowrap' }}>
          <TextField
            placeholder="Cerca per nominativo o email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            sx={{ flex: 1, minWidth: 260 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={16} />
                </InputAdornment>
              )
            }}
          />

          <TextField
            label="Al"
            type="date"
            size="small"
            value={referenceDate}
            onChange={(e) => {
              const value = e.target.value;
              setReferenceDate(value);
              fetchData({
                dateOverride: value,
                contabileSearchOverride: search
              });
            }}
            sx={{ width: 160 }}
            InputLabelProps={{ shrink: true }}
          />

          <Tooltip
            title="Somma dei dossier distinti su tutti i contabili nel perimetro (pratiche assegnate fino alla data selezionata)."
            arrow
          >
            <Chip
              label={`Totale contratti: ${totalContratti.toLocaleString('it-IT')}`}
              sx={{ borderRadius: '16px', fontWeight: 800 }}
              variant="outlined"
            />
          </Tooltip>
          <Tooltip
            title="Somma dei clienti distinti su tutti i contabili nel perimetro (pratiche assegnate fino alla data selezionata)."
            arrow
          >
            <Chip
              label={`Totale clienti: ${totalClienti.toLocaleString('it-IT')}`}
              sx={{ borderRadius: '16px', fontWeight: 800 }}
              variant="outlined"
            />
          </Tooltip>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'contabile_name'}
                    direction={orderBy === 'contabile_name' ? order : 'asc'}
                    onClick={() => handleRequestSort('contabile_name')}
                  >
                    <HeaderHelp
                      label="Referente contabile"
                      help="Raggruppa per referente contabile (rif_responsabile_contabile sul dossier). I valori sono calcolati sui dossier che hanno pratiche assegnate fino alla data selezionata."
                    />
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    active={orderBy === 'contratti_totale'}
                    direction={orderBy === 'contratti_totale' ? order : 'asc'}
                    onClick={() => handleRequestSort('contratti_totale')}
                  >
                    <HeaderHelp
                      label="Totale contratti"
                      help="COUNT(DISTINCT dossier). Include tutti i dossier del contabile che hanno almeno una pratica assegnata fino alla data selezionata (soft_delete=0), indipendentemente dallo stato della pratica."
                    />
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    active={orderBy === 'contratti_con_recesso'}
                    direction={orderBy === 'contratti_con_recesso' ? order : 'asc'}
                    onClick={() => handleRequestSort('contratti_con_recesso')}
                  >
                    <HeaderHelp
                      label="Contratti con recesso"
                      help="COUNT(DISTINCT dossier) tra quelli del contabile in cui il dossier ha status='failed' e motivazione_fallimento in (lasciare, recesso_nei_tempi, perseguire)."
                    />
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    active={orderBy === 'contratti_chiusi'}
                    direction={orderBy === 'contratti_chiusi' ? order : 'asc'}
                    onClick={() => handleRequestSort('contratti_chiusi')}
                  >
                    <HeaderHelp
                      label="Contratti chiusi"
                      help="COUNT(DISTINCT dossier) del contabile in cui TUTTE le pratiche (negoziazioni) del dossier nel perimetro sono status='closed' (fino alla data selezionata)."
                    />
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    active={orderBy === 'contratti_multi_legali'}
                    direction={orderBy === 'contratti_multi_legali' ? order : 'asc'}
                    onClick={() => handleRequestSort('contratti_multi_legali')}
                  >
                    <HeaderHelp
                      label="Multi-legali"
                      help="COUNT(DISTINCT dossier) del contabile che hanno 2+ legali distinti (COUNT DISTINCT rif_responsabile_legale) sulle negotiation del dossier con soft_delete=0."
                    />
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    active={orderBy === 'clienti_totale'}
                    direction={orderBy === 'clienti_totale' ? order : 'asc'}
                    onClick={() => handleRequestSort('clienti_totale')}
                  >
                    <HeaderHelp
                      label="Totale clienti"
                      help="COUNT(DISTINCT cliente) sui dossier del contabile nel perimetro (pratiche assegnate fino alla data)."
                    />
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    active={orderBy === 'clienti_con_recesso'}
                    direction={orderBy === 'clienti_con_recesso' ? order : 'asc'}
                    onClick={() => handleRequestSort('clienti_con_recesso')}
                  >
                    <HeaderHelp
                      label="Clienti recessi"
                      help="COUNT(DISTINCT cliente) associati a dossier in recesso (dossier failed + motivazione_fallimento in whitelist) nel perimetro del contabile."
                    />
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    active={orderBy === 'clienti_gestiti'}
                    direction={orderBy === 'clienti_gestiti' ? order : 'asc'}
                    onClick={() => handleRequestSort('clienti_gestiti')}
                  >
                    <HeaderHelp
                      label="Clienti gestiti"
                      help="Calcolato come: Totale clienti − Clienti recessi."
                    />
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    active={orderBy === 'clienti_percentuale'}
                    direction={orderBy === 'clienti_percentuale' ? order : 'asc'}
                    onClick={() => handleRequestSort('clienti_percentuale')}
                  >
                    <HeaderHelp
                      label="% clienti"
                      help="Percentuale di clienti del contabile sul totale complessivo (Totale clienti riga / Totale clienti globale * 100)."
                    />
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 900 }}>
                  <HeaderHelp
                    label="Azioni"
                    help="Consente di scaricare il PDF di dettaglio per il contabile selezionato."
                  />
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : paginatedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                    <Typography color="text.secondary">Nessun dato disponibile</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedRows.map((row) => {
                  const contabileId = Number(row?.contabile_id) || 0;
                  const isExpanded = expandedContabileId === contabileId;
                  const name = row?.contabile_name || 'Senza contabile';
                  const email = row?.contabile_email || '';
                  const rawPercent = Number(row?.clienti_percentuale);
                  const safePercent = Number.isFinite(rawPercent) ? rawPercent : 0;
                  const clampedPercent = Math.max(0, Math.min(100, safePercent));
                  const contractsItems = contractsByContabileId[contabileId] || [];
                  const contractsLoading = Boolean(contractsLoadingByContabileId[contabileId]);

                  return (
                    <Fragment key={contabileId || name}>
                      <TableRow hover sx={{ cursor: 'default' }}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                            <IconButton
                              size="small"
                              disabled={!contabileId}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleContracts(row);
                              }}
                            >
                              {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                            </IconButton>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography sx={{ fontWeight: 900, lineHeight: 1.15 }}>{name}</Typography>
                              {email ? (
                                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.15 }}>
                                  {email}
                                </Typography>
                              ) : null}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={Number(row?.contratti_totale || 0).toLocaleString('it-IT')}
                            sx={{ borderRadius: '16px', fontWeight: 800 }}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={Number(row?.contratti_con_recesso || 0).toLocaleString('it-IT')}
                            sx={{ borderRadius: '16px', fontWeight: 800, color: '#dc2626', borderColor: '#fecaca' }}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={Number(row?.contratti_chiusi || 0).toLocaleString('it-IT')}
                            sx={{ borderRadius: '16px', fontWeight: 800, color: '#2563eb', borderColor: '#bfdbfe' }}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={Number(row?.contratti_multi_legali || 0).toLocaleString('it-IT')}
                            sx={{ borderRadius: '16px', fontWeight: 800, color: '#f59e0b', borderColor: '#fde68a' }}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={Number(row?.clienti_totale || 0).toLocaleString('it-IT')}
                            sx={{ borderRadius: '16px', fontWeight: 800 }}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={Number(row?.clienti_con_recesso || 0).toLocaleString('it-IT')}
                            sx={{ borderRadius: '16px', fontWeight: 800, color: '#dc2626', borderColor: '#fecaca' }}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={Number(row?.clienti_gestiti || 0).toLocaleString('it-IT')}
                            sx={{ borderRadius: '16px', fontWeight: 800, color: '#16a34a', borderColor: '#bbf7d0' }}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                              <CircularProgress
                                variant="determinate"
                                value={100}
                                size={36}
                                thickness={6}
                                sx={{ color: 'rgba(145, 158, 171, 0.24)' }}
                              />
                              <CircularProgress
                                variant="determinate"
                                value={clampedPercent}
                                size={36}
                                thickness={6}
                                sx={{ position: 'absolute', left: 0 }}
                                color="warning"
                              />
                              <Box
                                sx={{
                                  top: 0,
                                  left: 0,
                                  bottom: 0,
                                  right: 0,
                                  position: 'absolute',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.primary', fontSize: '0.7rem' }}>
                                  {safePercent.toFixed(0)}%
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Tooltip title="Report PDF (dettaglio contabile)" arrow>
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={!contabileId || Boolean(rowPdfLoadingByContabileId[contabileId])}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleStartContabileDetailPdfJob(row);
                                  }}
                                >
                                  <FileText size={18} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell colSpan={10} sx={{ p: 0, borderBottom: 0 }}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ p: 2, pt: 1.5, bgcolor: 'rgba(145, 158, 171, 0.06)' }}>
                              <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                  Dettaglio contratti
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Contratti: {Number(row?.contratti_totale || 0).toLocaleString('it-IT')} · con recesso:{' '}
                                  {Number(row?.contratti_con_recesso || 0).toLocaleString('it-IT')} · chiusi:{' '}
                                  {Number(row?.contratti_chiusi || 0).toLocaleString('it-IT')} · multi-legali:{' '}
                                  {Number(row?.contratti_multi_legali || 0).toLocaleString('it-IT')} · Clienti:{' '}
                                  {Number(row?.clienti_totale || 0).toLocaleString('it-IT')} · Clienti recessi:{' '}
                                  {Number(row?.clienti_con_recesso || 0).toLocaleString('it-IT')} · Clienti gestiti:{' '}
                                  {Number(row?.clienti_gestiti || 0).toLocaleString('it-IT')} · % clienti: {safePercent.toFixed(0)}%
                                </Typography>
                              </Box>

                              {contractsLoading ? (
                                <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
                                  <CircularProgress size={20} />
                                </Box>
                              ) : contractsItems.length === 0 ? (
                                <Box sx={{ py: 2 }}>
                                  <Typography variant="body2" color="text.secondary">
                                    Nessun dettaglio disponibile
                                  </Typography>
                                </Box>
                              ) : (
                                <Table size="small" sx={{ mt: 1 }}>
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Tipo</TableCell>
                                      <TableCell align="right">Pratiche</TableCell>
                                      <TableCell>Cliente</TableCell>
                                      <TableCell align="right">Conferimento</TableCell>
                                      <TableCell align="right">Importo</TableCell>
                                      <TableCell align="center">Stato</TableCell>
                                      <TableCell align="center">Recesso</TableCell>
                                      <TableCell align="center">Data recesso</TableCell>
                                      <TableCell align="center">Multi-legali</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {contractsItems.map((r) => {
                                      const tipoContratto = r?.tipo_contratto ? String(r.tipo_contratto) : '-';
                                      const praticheTotale = Number(r?.pratiche_totale || 0);
                                      const customerName = r?.customer_name || 'Cliente';
                                      const customerEmail = r?.customer_email || '';
                                      const customerPhone = r?.customer_phone || '';
                                      const conferimento = formatDate(r?.data_conferimento);
                                      const importo = formatCurrency(r?.importo_totale);
                                      const stato = r?.dossier_status || r?.stato_preventivo || '-';
                                      const legalCount = Number(r?.legal_count || 0);
                                      const hasRecesso = Boolean(r?.has_recesso);
                                      const recessoDate = formatDate(r?.recesso_date);
                                      const isMulti = Boolean(r?.is_multi_legali);
                                      const legalNames = Array.isArray(r?.legali_names) ? r.legali_names : [];
                                      const dossierKey = r?.dossier_reference || r?.dossier_id || customerName;
                                      return (
                                        <TableRow key={String(dossierKey)} hover>
                                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{tipoContratto}</TableCell>
                                          <TableCell align="right">
                                            <Typography variant="body2">{praticheTotale.toLocaleString('it-IT')}</Typography>
                                          </TableCell>
                                          <TableCell sx={{ minWidth: 220 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 800, lineHeight: 1.15, fontSize: '0.85rem' }}>
                                              {customerName}
                                            </Typography>
                                            {customerPhone ? (
                                              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.15 }}>
                                                {customerPhone}
                                              </Typography>
                                            ) : null}
                                            {customerEmail ? (
                                              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.15 }}>
                                                {customerEmail}
                                              </Typography>
                                            ) : null}
                                          </TableCell>
                                          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                            {conferimento}
                                          </TableCell>
                                          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                            {importo}
                                          </TableCell>
                                          <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                                            {stato}
                                          </TableCell>
                                          <TableCell align="center">
                                            {hasRecesso ? (
                                              <Chip
                                                label="Recesso"
                                                sx={{
                                                  borderRadius: '16px',
                                                  fontWeight: 800,
                                                  color: '#dc2626',
                                                  borderColor: '#fecaca'
                                                }}
                                                variant="outlined"
                                              />
                                            ) : (
                                              <Typography variant="body2" color="text.secondary">
                                                -
                                              </Typography>
                                            )}
                                          </TableCell>
                                          <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                                            {hasRecesso ? recessoDate : '-'}
                                          </TableCell>
                                          <TableCell align="center">
                                            {isMulti ? (
                                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                                                <Chip
                                                  label={`Sì (${legalCount})`}
                                                  sx={{
                                                    borderRadius: '16px',
                                                    fontWeight: 800,
                                                    color: '#f59e0b',
                                                    borderColor: '#fde68a'
                                                  }}
                                                  variant="outlined"
                                                />
                                                {legalNames.length > 0 ? (
                                                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.2, textAlign: 'center' }}>
                                                    {legalNames.join(', ')}
                                                  </Typography>
                                                ) : null}
                                              </Box>
                                            ) : (
                                              <Typography variant="body2" color="text.secondary">
                                                No
                                              </Typography>
                                            )}
                                          </TableCell>
                                        </TableRow>
                                      );
                                    })}
                                  </TableBody>
                                </Table>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={sortedRows.length}
          page={page}
          onPageChange={(_e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage="Righe per pagina"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} di ${count !== -1 ? count : `più di ${to}`}`}
        />
      </StyledCard>
    </Box>
  );
}
